import React, { useMemo, useState } from 'react';

import { ActionDropdown } from '@/components';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Tag } from '@/design-system/tag';
import { toast } from '@/design-system/toaster';
import { useUrlBool } from '@/hooks/urlState';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import { OccupancyBadge, OccupancyBar } from '../../components/OccupancyBadge';
import { HeaderActions } from '../../components/pickers';
import StorageFormModal from '../../components/StorageFormModal';
import {
  Dash,
  HubRequired,
  LoadError,
  metaCls,
  nameCls,
  numCls,
  PageHeader,
} from '../../components/ui';
import { downloadBlob, fmtQty } from '../../format';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { STORAGE_FILTERS } from '../../filterFields';
import { NO_TABS, useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { storagePathOf, storageTypeOf } from '../../hooks/useStorages';
import { fmtCbm, fmtPct } from '../../occupancy';
import { STORAGE_TYPE_LABEL } from '../../status';
import type { WarehouseStorage } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { CapacityImportModal } from './CapacityImportModal';

/**
 * One storage per row, flat: a location fills only Location, a section
 * Location + Section, a bin all three — the level above a row is its lineage,
 * the row's own name sits in its own level's column.
 */
export const storageLevels = (
  s: WarehouseStorage
): Record<'location' | 'section' | 'bin', string | null> => ({
  location: s.category === 'LOCATION' ? s.name : (s.location?.name ?? null),
  section:
    s.category === 'SECTION'
      ? s.name
      : s.category === 'BIN'
        ? (s.section?.name ?? null)
        : null,
  bin: s.category === 'BIN' ? s.name : null,
});

/** On-hand shown only when the server sent it; a differing direct qty goes under it. */
const direct = (s: WarehouseStorage): number | null =>
  typeof s.qtyOnHandDirect === 'number' &&
  s.qtyOnHandDirect > 0 &&
  s.qtyOnHandDirect !== s.qtyOnHand
    ? s.qtyOnHandDirect
    : null;

const LevelCell: React.FC<{ name: string | null }> = ({ name }) =>
  name ? <span className={nameCls}>{name}</span> : <Dash />;

const LEVEL_COLUMNS: TableDataColumn<WarehouseStorage>[] = (
  [
    ['location', 'Location'],
    ['section', 'Section'],
    ['bin', 'Bin'],
  ] as const
).map(([level, title]) => ({
  key: level,
  title,
  render: (_v, s) => <LevelCell name={storageLevels(s)[level]} />,
}));

/** A bin with no m³ capacity ("belum diset"); the filter and the Okupansi alert are about these. */
export const isCapacityUnset = (s: WarehouseStorage): boolean =>
  s.category === 'BIN' &&
  (s.capacityCbm === null || s.capacityCbm === undefined);

const CapacityCell: React.FC<{ s: WarehouseStorage }> = ({ s }) => {
  if (typeof s.capacityCbm === 'number') {
    const extra = [
      typeof s.capacityKg === 'number' ? `${fmtQty(s.capacityKg)} kg` : null,
      typeof s.capacityQty === 'number' ? `${fmtQty(s.capacityQty)} qty` : null,
    ].filter(Boolean);
    return (
      <div data-testid={`storage-cap-${s.id}`}>
        <p className={numCls}>{fmtCbm(s.capacityCbm)}</p>
        {extra.length > 0 && <p className={metaCls}>{extra.join(' · ')}</p>}
      </div>
    );
  }
  return isCapacityUnset(s) ? (
    <span
      className="text-[12px] text-text-sub-600"
      data-testid={`storage-cap-${s.id}`}
    >
      Belum diset
    </span>
  ) : (
    <Dash />
  );
};

const PctCell: React.FC<{ s: WarehouseStorage }> = ({ s }) =>
  typeof s.pctCbm === 'number' ? (
    <div
      className="flex min-w-[96px] flex-col gap-1"
      data-testid={`storage-pct-${s.id}`}
    >
      <span className={numCls}>{fmtPct(s.pctCbm)}</span>
      <OccupancyBar pct={s.pctCbm} label={`Okupansi ${s.name}`} />
    </div>
  ) : (
    <Dash />
  );

const StoragePage: React.FC = () => {
  const { hubID, hub, ready, refreshToken, refresh } = useWarehouse();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<WarehouseStorage | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // "Belum diset": bins without a capacity. Kept in the URL so the Okupansi
  // alert can link here; applied to the rows of the page that is open.
  const [unset, setUnset] = useUrlBool('unset', false, { resetPage: true });
  const enabled = ready && hubID !== null;
  const q = useListQuery(NO_TABS, 'ALL', STORAGE_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;
  const filtered = !!search || q.filters.count > 0;

  // Server-paged, in tree order (siblings stay grouped); each row carries its
  // on-hand qty rolled up over its subtree (withStock).
  const list = useLoad(
    (signal) =>
      warehouseApi.listStoragesPage(
        {
          hubID: hubID as number,
          withStock: true,
          // Only bins can lack a capacity: page through just those.
          ...(unset && !filterApi.category ? { category: 'BIN' } : {}),
          ...filterApi,
          search,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, filterKey, search, q.page, q.limit, refreshToken, unset],
    enabled
  );
  const rows = useMemo(
    () =>
      unset
        ? (list.data?.data ?? []).filter(isCapacityUnset)
        : (list.data?.data ?? []),
    [list.data, unset]
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await warehouseApi.downloadStorageCapacityCsv(
        hubID as number
      );
      downloadBlob(blob, filename);
      toast.success('CSV kapasitas berhasil di-download.');
    } catch (err) {
      toast.error('CSV kapasitas gagal di-download', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<TableDataColumn<WarehouseStorage>[]>(
    () => [
      ...LEVEL_COLUMNS,
      {
        key: 'type',
        title: 'Tipe',
        render: (_v, s) => (
          <Tag size="xs" variant="gray">
            {STORAGE_TYPE_LABEL[storageTypeOf(s)]}
          </Tag>
        ),
      },
      {
        key: 'hub',
        title: 'Hub',
        render: (_v, s) => (
          <span className={nameCls}>
            {s.hub?.name ?? hub?.name ?? `Hub #${s.hubID}`}
          </span>
        ),
      },
      {
        key: 'onHand',
        title: 'On hand',
        align: 'right',
        render: (_v, s) => {
          if (typeof s.qtyOnHand !== 'number') return <Dash />;
          const own = direct(s);
          return (
            <div data-testid={`storage-onhand-${s.id}`}>
              <p className={numCls}>{fmtQty(s.qtyOnHand)}</p>
              {own !== null && (
                <p className={metaCls}>{fmtQty(own)} langsung di sini</p>
              )}
            </div>
          );
        },
      },
      {
        key: 'capacity',
        title: 'Kapasitas (m³)',
        align: 'right',
        render: (_v, s) => <CapacityCell s={s} />,
      },
      {
        key: 'used',
        title: 'Terpakai (m³)',
        align: 'right',
        render: (_v, s) =>
          typeof s.usedCbm === 'number' ? (
            <span className={numCls} data-testid={`storage-used-${s.id}`}>
              {fmtCbm(s.usedCbm)}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'pct',
        title: 'Okupansi',
        render: (_v, s) => <PctCell s={s} />,
      },
      {
        key: 'occupancy',
        title: 'Status okupansi',
        render: (_v, s) => (
          <OccupancyBadge
            status={s.occupancyStatus}
            testId={`storage-occ-${s.id}`}
          />
        ),
      },
      {
        key: 'state',
        title: 'Status',
        render: (_v, s) => (
          <Badge
            size="sm"
            type="dot"
            status={s.status === 'ACTIVE' ? 'success' : 'faded'}
          >
            {s.status === 'ACTIVE' ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, s) => (
          <ActionDropdown
            ariaLabel={`Aksi ${storagePathOf(s)}`}
            actions={[
              {
                key: 'edit',
                label: 'Edit',
                icon: 'ri-pencil-line',
                onClick: () => setEditing(s),
              },
            ]}
          />
        ),
      },
    ],
    [hub]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Warehouse management"
        title="Storage"
        actions={
          // Storage is per hub: no "Semua hub" that would only say "pilih hub dulu".
          <HeaderActions allowAll={false}>
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setImporting(true)}
              disabled={!enabled}
              data-testid="storage-import-open"
              leftIcon={<i className="ri-upload-2-line text-[14px]" />}
            >
              Import CSV kapasitas
            </Button>
            <Button
              tone="neutral"
              style="stroke"
              onClick={exportCsv}
              loading={exporting}
              disabled={!enabled}
              data-testid="storage-export-csv"
              leftIcon={<i className="ri-download-2-line text-[14px]" />}
            >
              Export CSV
            </Button>
            <Button
              onClick={() => setCreating(true)}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Tambah storage
            </Button>
          </HeaderActions>
        }
      />
      {!enabled ? (
        <HubRequired />
      ) : (
        <ListShell
          filters={
            <>
              <ListSearch
                value={q.search.value}
                onChange={q.search.setValue}
                placeholder="Cari nama storage…"
              />
              <Button
                tone={unset ? 'primary' : 'neutral'}
                style={unset ? 'lighter' : 'stroke'}
                size="sm"
                aria-pressed={unset}
                data-testid="storage-unset-toggle"
                onClick={() => setUnset(!unset)}
                leftIcon={<i className="ri-error-warning-line text-[14px]" />}
              >
                Belum diset
              </Button>
              <WarehouseFilterPanel
                filters={q.filters}
                title="Filter storage"
              />
            </>
          }
          pagination={{
            page: q.page,
            limit: q.limit,
            total: list.data?.pagination.total ?? 0,
            onPageChange: q.setPage,
            onLimitChange: q.setLimit,
          }}
        >
          {list.error ? (
            <LoadError message={list.error.message} onRetry={list.reload} />
          ) : (
            <TableData
              size="sm"
              loading={list.loading}
              data={rows}
              columns={columns}
              emptyState={
                unset ? (
                  <EmptyTable
                    title="Semua bin di halaman ini sudah punya kapasitas"
                    description="Filter ini menyaring baris di halaman yang terbuka; coba halaman lain."
                  />
                ) : filtered ? (
                  <EmptyTable title="Tidak ada storage yang cocok" />
                ) : (
                  <EmptyTable title="Belum ada storage di hub ini" />
                )
              }
            />
          )}
        </ListShell>
      )}
      <CapacityImportModal
        open={importing}
        hubID={hubID as number}
        onClose={() => setImporting(false)}
        onImported={() => refresh()}
      />
      <StorageFormModal
        open={creating}
        initialHubID={hubID}
        onClose={() => setCreating(false)}
        onCreated={(s) => {
          setCreating(false);
          toast.success('Storage dibuat', {
            description: storagePathOf(s),
          });
          refresh();
        }}
      />
      <StorageFormModal
        open={!!editing}
        initialHubID={hubID}
        storage={editing}
        onClose={() => setEditing(null)}
        onCreated={() => undefined}
        onUpdated={(s) => {
          setEditing(null);
          toast.success('Storage diperbarui', {
            description: storagePathOf(s),
          });
          refresh();
        }}
      />
    </div>
  );
};

export default StoragePage;
